import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Spinner, SpinnerOverlay } from '@quality24/design-system';
import { EnumServiceCategory as Category } from '@quality24/inpatient-typings';
import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import Can from '@/inpatient-patient-pwa/atoms/Can';
import ApiError from '@/inpatient-patient-pwa/molecules/ApiError';
import PatientCallSection from '@/inpatient-patient-pwa/organisms/PatientCallSection';
import useTriggerOrderStore from '@/inpatient-patient-pwa/hooks/useTriggerOrder/store';

import { getFloorId } from '@/inpatient-patient-pwa/services/storage';

import { useAuth } from '@/inpatient-patient-pwa/contexts/auth/useAuth';
import {
  ALERT_SUBSCRIPTION,
  SELECT_GROUP_QUERY,
  SELECT_QUERY,
} from './graphql';
import { QueryResult } from './types';
import { handleAlertChanges, parseGroups } from './utils';

export interface Props {
  category?: Category;
}

const OrderTriggerPage: React.FunctionComponent<Props> = ({
  category,
}: Props) => {
  const { t } = useTranslation();
  const {
    user: { admissionId },
  } = useAuth();
  const floorId = getFloorId();
  const { serviceGroupId } = useParams<{ serviceGroupId?: string }>();
  const [searchParams] = useSearchParams();
  const groupTitle = searchParams.get('title') ?? undefined;

  const navigate = useNavigate();

  const submitting = useTriggerOrderStore((state) => state.loading);

  const selectQuery = serviceGroupId ? SELECT_GROUP_QUERY : SELECT_QUERY;
  const selectVariables = serviceGroupId
    ? { admissionId, groupId: serviceGroupId, floorId }
    : { admissionId, category, floorId };

  const { data, loading, error, subscribeToMore, refetch } =
    useQuery<QueryResult>(selectQuery, {
      variables: selectVariables,
      fetchPolicy: 'network-only',
    });

  React.useEffect(() => {
    const unsubscribe = subscribeToMore({
      document: ALERT_SUBSCRIPTION,
      variables: { admissionId },
      updateQuery: handleAlertChanges,
    });
    return unsubscribe;
  }, [admissionId, subscribeToMore]);

  const filteredGroups = React.useMemo(() => {
    if (!data) return [];
    if (data.groups?.nodes?.every((group) => group.isDefault)) {
      return data.groups.nodes;
    }
    return data.groups?.nodes?.filter((group) => !group.isDefault);
  }, [data]);

  const reasons = parseGroups(filteredGroups);

  return (
    <>
      <Helmet>
        <title>{t('home.pageTitle')}</title>
      </Helmet>

      {submitting && <SpinnerOverlay fixed />}

      <Can perform="patient-call-section:view">
        <div className="px-4">
          {loading && (
            <div className="d-flex flex-justify-center p-4">
              <Spinner />
            </div>
          )}

          {error && <ApiError error={error} refetch={refetch} />}

          {!loading && !error && (
            <PatientCallSection
              reasons={reasons}
              category={category ?? 'ASSISTANCE'}
              title={groupTitle}
              onTriggered={() => navigate('/calls')}
            />
          )}
        </div>
      </Can>
    </>
  );
};

export default OrderTriggerPage;
